import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import RoleTag from 'router/subrouters/DataRoom/subrouters/components/RoleTag';
import { COLOR_VARIANT } from 'models/Colors';
import {
  DATA_ROOM_SHAREABLE_LINK_ACCESS,
  DataRoomShareableLink,
} from 'models/ShareableLink';
import { DATE_FORMAT_DEFAULT, getFormattedDate } from 'dateUtils';
import ShareableLinkItems from '../../../../../../components/DataRoomDocumentsRouter/components/ShareableLinkDrawer/components/ShareableLinkItems';
import useSecureLinkSelectorNodes from '../../hooks/useSecureLinkSelectorNodes';

import './SecureLinkSummary.styles.scss';

type SecureLinkSummaryProps = {
  className?: string;
  // Absent until the BE nests the link on the queue row. The request still
  // resolves through it, so the option stays — it just has nothing to describe
  // beyond the name the row already carries.
  link?: DataRoomShareableLink;
  name?: string;
  // The drawer stays mounted while closed, so the tree is only fetched once the
  // seller is actually looking at it.
  isVisible?: boolean;
};

// How the link lets people in, phrased the way the secure links table phrases it.
const ACCESS_LABELS: Record<string, string> = {
  [DATA_ROOM_SHAREABLE_LINK_ACCESS.SPECIFIC_RECIPIENTS]:
    'specificRecipientsOnly',
  [DATA_ROOM_SHAREABLE_LINK_ACCESS.ADD_PASSWORD]: 'passwordProtected',
  [DATA_ROOM_SHAREABLE_LINK_ACCESS.ANYONE_WITH_LINK]: 'anyoneWithTheLink',
};

const SecureLinkSummary: React.FC<SecureLinkSummaryProps> = (props) => {
  const { className, link, name, isVisible = false } = props;

  const { t } = useTranslation();

  const {
    name: linkName,
    linkAccess,
    allowDownloads,
    linkExpiresDate,
  } = link ?? {};

  const { nodes, setNodes, initiallyCheckedNodes, isLoading } =
    useSecureLinkSelectorNodes(link, isVisible);

  const classes = classNames('secure-link-summary', className);

  const pills = useMemo(
    () =>
      [
        t(ACCESS_LABELS[linkAccess]),
        t(allowDownloads ? 'downloadOn' : 'downloadOff'),
        linkExpiresDate
          ? t('expiresOnDate', {
              date: getFormattedDate(linkExpiresDate, DATE_FORMAT_DEFAULT),
            })
          : t('neverExpires'),
      ].filter(Boolean),
    [allowDownloads, linkAccess, linkExpiresDate, t],
  );

  return (
    <div className={classes}>
      <p className="secure-link-summary__name">{linkName || name}</p>

      {/* How the link is locked down, and what it holds. Both come off the link
      itself, so neither is shown until the row carries it. */}
      {!!link && (
        <>
          <div className="secure-link-summary__pills">
            {pills.map((pill) => (
              <RoleTag key={pill} variant={COLOR_VARIANT.SILVER}>
                {pill}
              </RoleTag>
            ))}
          </div>

          <ShareableLinkItems
            className="secure-link-summary__items"
            readOnly
            isLoading={isLoading}
            nodes={nodes}
            initiallyCheckedNodes={initiallyCheckedNodes}
            setNodes={setNodes}
          />

          <p className="secure-link-summary__note">
            {t('secureLinkReadOnlyNote')}
          </p>
        </>
      )}
    </div>
  );
};

export default SecureLinkSummary;
